import type { AdminOrgMemberDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../../utils/format';
import { initials } from '../../../utils/initials';
import { PiiText } from '../../pickers/_components/PiiText';

/**
 * The members table of «Сотрудники» (AUT-403). The list is small (seat cap)
 * and unpaginated on the wire, so a plain table, not the data grid.
 */
export interface MembersTableProps {
  members: AdminOrgMemberDto[];
  actionsEnabled: boolean;
  onRemove: (member: AdminOrgMemberDto) => void;
  onChangeOwner: () => void;
}

const StatusCell = ({ member }: { member: AdminOrgMemberDto }): ReactElement => {
  const { t } = useTranslation();
  if (member.status === 'active') {
    return <Chip size="small" color="success" label={t('companies.members.state.active')} />;
  }
  const reason = t(`companies.members.removed.${member.removedReason ?? 'admin'}`);
  return (
    <Typography variant="body2" color="text.secondary">
      {member.removedAt ? `${reason} · ${formatDate(member.removedAt)}` : reason}
    </Typography>
  );
};

const ActionCell = ({
  member,
  enabled,
  onRemove,
  onChangeOwner,
}: {
  member: AdminOrgMemberDto;
  enabled: boolean;
  onRemove: (member: AdminOrgMemberDto) => void;
  onChangeOwner: () => void;
}): ReactElement | null => {
  const { t } = useTranslation();
  if (!enabled || member.status !== 'active') return null;
  if (member.role === 'owner') {
    return (
      <Button size="small" onClick={onChangeOwner}>
        {t('companies.members.changeOwner')}
      </Button>
    );
  }
  return (
    <Button size="small" color="error" onClick={() => onRemove(member)}>
      {t('companies.members.remove')}
    </Button>
  );
};

export const MembersTable = ({
  members,
  actionsEnabled,
  onRemove,
  onChangeOwner,
}: MembersTableProps): ReactElement => {
  const { t } = useTranslation();
  const ns = 'companies.members';
  if (members.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(`${ns}.empty`)}
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {(['member', 'phone', 'role', 'joinedAt', 'status'] as const).map((col) => (
            <TableCell key={col}>{t(`${ns}.columns.${col}`)}</TableCell>
          ))}
          <TableCell aria-label={t(`${ns}.columns.actions`)} />
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((m) => (
          <TableRow
            key={m.userId}
            sx={m.status === 'removed' ? { opacity: 0.6 } : undefined}
            data-testid={`company-member-row-${m.userId}`}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700 }}>
                  {initials(m.name)}
                </Avatar>
                <Typography variant="body2" fontWeight={600}>
                  {m.name ?? t('companies.noValue')}
                </Typography>
              </Box>
            </TableCell>
            <TableCell data-testid="company-member-phone">
              <PiiText value={m.phone} mono />
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                color={m.role === 'owner' ? 'primary' : 'default'}
                label={t(`${ns}.role.${m.role}`)}
              />
            </TableCell>
            <TableCell>{formatDate(m.joinedAt)}</TableCell>
            <TableCell>
              <StatusCell member={m} />
            </TableCell>
            <TableCell align="right">
              <ActionCell
                member={m}
                enabled={actionsEnabled}
                onRemove={onRemove}
                onChangeOwner={onChangeOwner}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
